import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  LogOut,
  User,
  Mail,
  Phone,
  Package,
  ClipboardCheck,
  ShieldCheck,
  FileText,
  ChevronRight,
  BellRing,
} from "lucide-react";
import { ScreenHeader, Card } from "./MobileShell";
import DeleteAccountCard from "./DeleteAccountCard";
import { useClientAuth } from "../hooks/useClientAuth";
import {
  clientListLabs,
  clientLogoutAll,
  getNotificationPrefs,
  setNotificationPrefs,
} from "../lib/api";
import {
  pushSupported,
  currentSubscription,
  enablePush,
  disablePush,
} from "./lib/push";

export default function ProfileScreen() {
  const { profile, token } = useOutletContext();
  const { logout } = useClientAuth();
  const [prefs, setPrefs] = useState({ session_reminders: true, plan_reminders: true });
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [labsCount, setLabsCount] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushErr, setPushErr] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [labs, p, sub] = await Promise.all([
          clientListLabs(token).catch(() => []),
          getNotificationPrefs(token).catch(() => ({ session_reminders: true, plan_reminders: true })),
          currentSubscription().catch(() => null),
        ]);
        if (!active) return;
        setLabsCount(labs.length);
        setPrefs({
          session_reminders: p.session_reminders !== false,
          plan_reminders: p.plan_reminders !== false,
        });
        setPushOn(!!sub);
      } finally {
        if (active) setPrefsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const savePref = async (next) => {
    const merged = { ...prefs, ...next };
    setPrefs(merged);
    try {
      await setNotificationPrefs(token, next);
    } catch {
      /* keep optimistic value */
    }
  };

  const togglePush = async (desired) => {
    setPushErr("");
    try {
      if (desired) {
        await enablePush(token);
        setPushOn(true);
      } else {
        await disablePush(token);
        setPushOn(false);
      }
    } catch (err) {
      setPushErr(err?.message || "Could not change push setting");
    }
  };

  return (
    <>
      <ScreenHeader eyebrow="Profile" title={profile?.name || "Member"} />

      <Card className="mb-4" data-testid="profile-identity">
        <ul className="divide-y divide-[var(--lc-line)] -m-4">
          <Row icon={User} label="Name" value={profile?.name} />
          <Row icon={Mail} label="Email" value={profile?.email || "—"} />
          <Row icon={Phone} label="Phone" value={profile?.phone || "—"} />
        </ul>
      </Card>

      <Card className="mb-4" data-testid="profile-plan">
        <LinkRow icon={Package} label="Current package" value={profile?.package_name || "Performance"} to="/packages" />
        <LinkRow icon={ClipboardCheck} label="Longevity assessment" value="Start or review" to="/assessment" />
        <LinkRow icon={FileText} label="Lab results" value={labsCount === null ? "..." : `${labsCount} on file`} to="/portal" />
      </Card>

      <Card className="mb-4" data-testid="profile-notifications">
        <div className="eyebrow text-[var(--lc-muted)] mb-2">Reminders</div>

        {pushSupported() ? (
          <div className="flex items-start justify-between gap-3 py-2.5 border-b border-[var(--lc-line)]">
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1.5">
                <BellRing className="h-3.5 w-3.5 text-[var(--lc-moss-dark)]" />
                Push notifications
              </div>
              <div className="text-[10px] text-[var(--lc-muted)] mt-0.5 leading-snug">
                {pushOn
                  ? "Enabled on this device"
                  : "Enable to receive session + plan nudges on this device"}
              </div>
              {pushErr && (
                <div className="text-[11px] text-red-700 mt-1" data-testid="profile-push-error">
                  {pushErr}
                </div>
              )}
            </div>
            <Toggle checked={pushOn} onChange={togglePush} testid="pref-push" />
          </div>
        ) : (
          <div className="text-xs text-[var(--lc-muted)] mb-2">
            This device doesn't support push notifications. Add to Home Screen first on iPhone.
          </div>
        )}

        <Toggle
          as="row"
          label="Upcoming session reminder (30 min before)"
          checked={prefs.session_reminders}
          onChange={(v) => savePref({ session_reminders: v })}
          disabled={prefsLoading}
          testid="pref-session-reminders"
        />
        <Toggle
          as="row"
          label="Plan activity reminder (daily 09:00 UTC)"
          checked={prefs.plan_reminders}
          onChange={(v) => savePref({ plan_reminders: v })}
          disabled={prefsLoading}
          testid="pref-plan-reminders"
        />

        <p className="text-[10px] text-[var(--lc-muted)] mt-2">
          Turn push off any time — your preferences stay saved on the server.
        </p>
      </Card>

      <Card className="mb-5" data-testid="profile-policies">
        <LinkRow icon={ShieldCheck} label="Privacy" value="How we handle your data" to="/privacy" />
        <LinkRow icon={FileText} label="Terms" value="Service & cancellation" to="/terms" />
      </Card>

      <DeleteAccountCard token={token} onDeleted={() => { logout(); window.location.replace("/m/login?deleted=1"); }} />

      <button
        onClick={logout}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-white border border-[var(--lc-line)] text-[12px] font-bold tracking-[0.18em] uppercase rounded-md hover:border-red-500 hover:text-red-700 transition-colors"
        data-testid="profile-logout"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>
      <button
        onClick={async () => {
          if (!window.confirm("Sign out of LONGEV on every device, including this one?")) return;
          await clientLogoutAll(token).catch(() => null);
          logout();
        }}
        className="mt-2 w-full text-[11px] font-bold tracking-[0.14em] uppercase text-[var(--lc-muted)] py-2"
        data-testid="profile-logout-all"
      >
        Sign out of all devices
      </button>

      <p className="mt-5 text-[10px] text-center text-[var(--lc-muted)] tracking-[0.14em] uppercase">
        LONGEV CIRCLE · Basel · v1.0
      </p>
    </>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Icon className="h-4 w-4 text-[var(--lc-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="eyebrow text-[var(--lc-muted)]">{label}</div>
        <div className="text-sm mt-0.5 truncate">{value}</div>
      </div>
    </li>
  );
}

function LinkRow({ icon: Icon, label, value, to }) {
  const Comp = to.startsWith("/") && !to.startsWith("/#") ? Link : "a";
  const props = Comp === Link ? { to } : { href: to };
  return (
    <Comp
      {...props}
      className="-mx-4 flex items-center gap-3 px-4 py-3 hover:bg-[var(--lc-cream)]/60 border-b border-[var(--lc-line)] last:border-b-0 first:-mt-4"
    >
      <Icon className="h-4 w-4 text-[var(--lc-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-[var(--lc-muted)] mt-0.5">{value}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-[var(--lc-muted)]" />
    </Comp>
  );
}

function Toggle({ as, label, checked, onChange, disabled, testid }) {
  const btn = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
        checked ? "bg-[var(--lc-moss)]" : "bg-[var(--lc-line)]"
      }`}
      data-testid={testid}
    >
      <span
        className={`inline-block h-5 w-5 transform bg-white rounded-full shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
  if (as === "row") {
    return (
      <label className="flex items-center justify-between gap-3 py-2.5">
        <span className="text-sm">{label}</span>
        {btn}
      </label>
    );
  }
  return btn;
}
